import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, Car, Star, MapPin } from "lucide-react";

const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-black text-white">
      {/* CTA Banner */}
      <div className="bg-[#AA6622] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 font-['Outfit']">
            Schaden gehabt? Wir helfen sofort!
          </h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            Rufen Sie uns an oder schreiben Sie per WhatsApp – wir sind täglich von 9:00 bis 23:00 Uhr für Sie da.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="tel:+491748936514"
              className="bg-white text-[#AA6622] px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-gray-100 transition-colors"
              data-testid="footer-cta-phone"
            >
              <Phone className="w-5 h-5" />
              <span>0174 893 6514</span>
            </a>
            <a
              href="https://wa.me/+491748936514"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] text-white px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#20bd5a] transition-colors"
              data-testid="footer-cta-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
              <span>WhatsApp Chat</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Logo & Info */}
            <div>
              <Link to="/" className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-[#AA6622] rounded-sm flex items-center justify-center">
                  <Car className="w-7 h-7 text-white" />
                </div>
                <div>
                  <span className="font-bold text-xl font-['Outfit']">Kfz Gutachter</span>
                  <span className="block text-sm text-gray-400">Schmeißer</span>
                </div>
              </Link>
              <p className="text-gray-400 leading-relaxed mb-4">
                Ihr unabhängiger Kfz-Sachverständiger in Hamburg und Schleswig-Holstein. Professionelle Gutachten für faire Entschädigung.
              </p>
              <div className="flex items-center space-x-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-[#AA6622] text-[#AA6622]" />
                  ))}
                </div>
                <span className="text-sm text-gray-400">5.0 bei Google</span>
              </div>
            </div>

            {/* Leistungen */}
            <div>
              <h4 className="font-bold text-lg mb-6 font-['Outfit']">Leistungen</h4>
              <ul className="space-y-3">
                <li><Link to="/leistungen/unfallgutachten" className="text-gray-400 hover:text-[#AA6622] transition-colors">Unfallgutachten</Link></li>
                <li><Link to="/leistungen/haftpflichtgutachten" className="text-gray-400 hover:text-[#AA6622] transition-colors">Haftpflichtgutachten</Link></li>
                <li><Link to="/leistungen/kasko-schaden" className="text-gray-400 hover:text-[#AA6622] transition-colors">Kasko-Schaden</Link></li>
                <li><Link to="/leistungen/kostenvoranschlag" className="text-gray-400 hover:text-[#AA6622] transition-colors">Kostenvoranschlag</Link></li>
                <li><Link to="/leistungen/wertgutachten" className="text-gray-400 hover:text-[#AA6622] transition-colors">Wertgutachten</Link></li>
                <li><Link to="/leistungen/oldtimer-gutachten" className="text-gray-400 hover:text-[#AA6622] transition-colors">Oldtimer-Gutachten</Link></li>
              </ul>
            </div>

            {/* Einsatzgebiete */}
            <div>
              <h4 className="font-bold text-lg mb-6 font-['Outfit']">Einsatzgebiete</h4>
              <ul className="space-y-3">
                <li><Link to="/einsatzgebiete/hamburg" className="text-gray-400 hover:text-[#AA6622] transition-colors flex items-center space-x-2"><MapPin className="w-4 h-4" /><span>Hamburg</span></Link></li>
                <li><Link to="/einsatzgebiete/pinneberg" className="text-gray-400 hover:text-[#AA6622] transition-colors flex items-center space-x-2"><MapPin className="w-4 h-4" /><span>Pinneberg</span></Link></li>
                <li><Link to="/einsatzgebiete/elmshorn" className="text-gray-400 hover:text-[#AA6622] transition-colors flex items-center space-x-2"><MapPin className="w-4 h-4" /><span>Elmshorn</span></Link></li>
                <li><Link to="/einsatzgebiete/wedel" className="text-gray-400 hover:text-[#AA6622] transition-colors flex items-center space-x-2"><MapPin className="w-4 h-4" /><span>Wedel</span></Link></li>
                <li><Link to="/einsatzgebiete/schleswig-holstein" className="text-gray-400 hover:text-[#AA6622] transition-colors flex items-center space-x-2"><MapPin className="w-4 h-4" /><span>Schleswig-Holstein</span></Link></li>
                <li><Link to="/einsatzgebiete" className="text-[#AA6622] hover:text-[#8e541b] transition-colors font-medium">Alle Einsatzgebiete →</Link></li>
              </ul>
            </div>

            {/* Kontakt */}
            <div>
              <h4 className="font-bold text-lg mb-6 font-['Outfit']">Kontakt</h4>
              <div className="space-y-4">
                <a href="tel:+491748936514" className="flex items-center space-x-3 text-gray-400 hover:text-[#AA6622] transition-colors">
                  <Phone className="w-5 h-5" />
                  <span>0174 893 6514</span>
                </a>
                <a href="https://wa.me/+491748936514" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 text-gray-400 hover:text-[#25D366] transition-colors">
                  <MessageCircle className="w-5 h-5" />
                  <span>WhatsApp</span>
                </a>
                <a href="mailto:info@bis-kfz-gutachten.de" className="flex items-center space-x-3 text-gray-400 hover:text-[#AA6622] transition-colors">
                  <Mail className="w-5 h-5" />
                  <span>info@bis-kfz-gutachten.de</span>
                </a>
              </div>
              <div className="mt-6 p-4 bg-white/5 rounded-sm">
                <p className="text-sm text-gray-400">
                  <span className="text-white font-medium">Erreichbarkeit:</span><br />
                  Mo – So: 09:00 – 23:00 Uhr
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Kfz Gutachter Schmeißer. Alle Rechte vorbehalten.
            </p>
            <div className="flex flex-wrap justify-center space-x-6 mt-4 md:mt-0">
              <Link to="/impressum" className="text-gray-400 hover:text-[#AA6622] text-sm transition-colors">Impressum</Link>
              <Link to="/datenschutz" className="text-gray-400 hover:text-[#AA6622] text-sm transition-colors">Datenschutz</Link>
              <Link to="/faq" className="text-gray-400 hover:text-[#AA6622] text-sm transition-colors">FAQ</Link>
              <Link to="/kontakt" className="text-gray-400 hover:text-[#AA6622] text-sm transition-colors">Kontakt</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
